import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useToast } from "@/components/SimpleToast";
import api from "@/lib/api";
import { useTaskCalendar } from "@/hooks/useTaskCalendar";

const EMPTY_FORM = {
  title: "",
  description: "",
  due_date: "",
  priority: "Mittel",
  status: "Offen",
  assigned_to_user_id: "",
  customer_id: "",
};

// Kapselt Daten-Laden, Filter, CRUD und Kalender-Logik der Aufgaben-Seite.
export function useTasks() {
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState("board");
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [editTask, setEditTask] = useState(null);
  const [saving, setSaving] = useState(false);
  const [myDayIds, setMyDayIds] = useState(new Set());

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterPriority, setFilterPriority] = useState("all");
  const [filterAssignee, setFilterAssignee] = useState("all");
  const [filterCustomer, setFilterCustomer] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);

  const [formData, setFormData] = useState(EMPTY_FORM);

  const openEditDialog = useCallback((task) => {
    setEditTask(task);
    setFormData({
      title: task.title,
      description: task.description || "",
      due_date: task.due_date || "",
      priority: task.priority,
      status: task.status,
      assigned_to_user_id: task.assigned_to_user_id || "",
      customer_id: task.customer_id || "",
    });
    setDialogOpen(true);
  }, []);

  const calendar = useTaskCalendar({ tasks, openEditDialog });
  const { fetchCalendarData, fetchSharedCalendars } = calendar;

  const fetchTasks = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (filterStatus !== "all") params.append("status", filterStatus);
      if (filterPriority !== "all") params.append("priority", filterPriority);
      if (filterAssignee !== "all") params.append("assigned_to", filterAssignee);
      if (filterCustomer !== "all") params.append("customer_id", filterCustomer);
      const response = await api.get(`/tasks?${params.toString()}`);
      setTasks(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Aufgaben");
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterAssignee, filterCustomer, toast]);

  const fetchUsers = useCallback(async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.warn("Benutzerliste konnte nicht geladen werden", error);
    }
  }, []);

  const fetchMyDayIds = useCallback(async () => {
    try {
      const res = await api.get("/my-day");
      const ids = Array.isArray(res.data?.tasks) ? res.data.tasks.map((t) => t.id) : [];
      setMyDayIds(new Set(ids));
    } catch (error) {
      console.warn("Mein-Tag-Marker konnten nicht geladen werden", error);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    try {
      const response = await api.get("/customers");
      setCustomers(response.data);
    } catch (error) {
      console.warn("Kundenliste konnte nicht geladen werden", error);
    }
  }, []);

  const handleToggleMyDay = useCallback(async (task) => {
    const inDay = myDayIds.has(task.id);
    setMyDayIds((prev) => {
      const next = new Set(prev);
      if (inDay) next.delete(task.id);
      else next.add(task.id);
      return next;
    });
    try {
      if (inDay) {
        await api.delete(`/my-day/${task.id}`);
        toast.success("Aus Mein Tag entfernt");
      } else {
        await api.post("/my-day", { task_id: task.id });
        toast.success("Zu Mein Tag hinzugefügt");
      }
    } catch (error) {
      setMyDayIds((prev) => {
        const next = new Set(prev);
        if (inDay) next.add(task.id);
        else next.delete(task.id);
        return next;
      });
      toast.error("Mein Tag konnte nicht aktualisiert werden");
    }
  }, [myDayIds, toast]);

  const handlePlanWeek = useCallback(async (task, dateKey, scope = "mine") => {
    try {
      await api.post("/week-plan", { task_id: task.id, date: dateKey, scope });
      const label = new Date(`${dateKey}T00:00:00`).toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });
      const where = scope === "team" ? "Team-Woche" : "Meine Woche";
      toast.success(`Eingeplant (${where}): ${label}`);
    } catch (error) {
      toast.error("Konnte nicht eingeplant werden");
    }
  }, [toast]);

  useEffect(() => {
    fetchTasks();
    fetchUsers();
    fetchCustomers();
    fetchMyDayIds();
    fetchSharedCalendars();
  }, [fetchTasks, fetchUsers, fetchCustomers, fetchMyDayIds, fetchSharedCalendars]);

  useEffect(() => {
    const onSync = () => fetchTasks();
    window.addEventListener("offline-sync-complete", onSync);
    return () => window.removeEventListener("offline-sync-complete", onSync);
  }, [fetchTasks]);

  useEffect(() => {
    const onChange = () => { fetchTasks(); fetchMyDayIds(); };
    window.addEventListener("tasks-changed", onChange);
    return () => window.removeEventListener("tasks-changed", onChange);
  }, [fetchTasks, fetchMyDayIds]);

  useEffect(() => {
    if (activeTab === "calendar") {
      fetchCalendarData();
    }
  }, [activeTab, fetchCalendarData]);

  // Sprachsteuerung: ?new=1 öffnet den Anlegen-Dialog (optional mit Titel/Kunde)
  useEffect(() => {
    if (searchParams.get("new")) {
      const title = searchParams.get("title");
      const customer = searchParams.get("customer");
      if (title || customer) {
        setFormData((prev) => ({
          ...prev,
          ...(title ? { title } : {}),
          ...(customer ? { customer_id: customer } : {}),
        }));
      }
      setDialogOpen(true);
      searchParams.delete("new");
      searchParams.delete("title");
      searchParams.delete("customer");
      setSearchParams(searchParams, { replace: true });
    }
    // KPI-Deeplink: ?overdue=1 zeigt nur überfällige Aufgaben
    if (searchParams.get("overdue")) {
      setOverdueOnly(true);
      searchParams.delete("overdue");
      setSearchParams(searchParams, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setEditTask(null);
  };

  const openCreateDialog = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editTask) {
        await api.put(`/tasks/${editTask.id}`, formData);
        toast.success("Aufgabe aktualisiert");
      } else {
        await api.post("/tasks", formData);
        toast.success("Aufgabe erstellt");
      }
      setDialogOpen(false);
      resetForm();
      fetchTasks();
      window.dispatchEvent(new Event("tasks-changed"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!taskToDelete) return;
    try {
      await api.delete(`/tasks/${taskToDelete.id}`);
      toast.success("Aufgabe gelöscht");
      setDeleteDialogOpen(false);
      setTaskToDelete(null);
      fetchTasks();
      window.dispatchEvent(new Event("tasks-changed"));
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
      toast.success("Status aktualisiert");
      fetchTasks();
      window.dispatchEvent(new Event("tasks-changed"));
    } catch (error) {
      toast.error("Fehler beim Aktualisieren");
    }
  };

  const handleSyncFromExchange = async () => {
    setSyncing(true);
    try {
      const response = await api.post("/exchange/tasks/sync");
      toast.success(response.data.message || "Aufgaben synchronisiert");
      fetchTasks();
      if (activeTab === "calendar") {
        fetchCalendarData();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Synchronisation fehlgeschlagen");
    } finally {
      setSyncing(false);
    }
  };

  const requestDelete = (t) => {
    setTaskToDelete(t);
    setDeleteDialogOpen(true);
  };

  const todayStr = new Date().toISOString().slice(0, 10);
  const isOverdue = (t) => t.status !== "Erledigt" && t.due_date && String(t.due_date).slice(0, 10) < todayStr;
  const displayTasks = overdueOnly ? tasks.filter(isOverdue) : tasks;

  const tasksByStatus = {
    "Offen": displayTasks.filter((t) => t.status === "Offen"),
    "In Bearbeitung": displayTasks.filter((t) => t.status === "In Bearbeitung"),
    "Erledigt": displayTasks.filter((t) => t.status === "Erledigt"),
  };

  return {
    calendar,
    activeTab, setActiveTab,
    tasks: displayTasks, users, customers, loading, syncing,
    dialogOpen, setDialogOpen,
    deleteDialogOpen, setDeleteDialogOpen,
    taskToDelete,
    editTask, saving, myDayIds,
    formData, setFormData,
    filterStatus, setFilterStatus,
    filterPriority, setFilterPriority,
    filterAssignee, setFilterAssignee,
    filterCustomer, setFilterCustomer,
    overdueOnly, setOverdueOnly,
    tasksByStatus,
    openEditDialog, openCreateDialog,
    handleSubmit, handleDelete, handleStatusChange,
    handleSyncFromExchange, handleToggleMyDay, handlePlanWeek,
    requestDelete,
  };
}
